import { useCallback, useEffect, useState } from "react";
import { LayoutGrid, ShieldCheck, Users, Inbox, GitBranch, IndianRupee, CalendarDays, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, fmtErr, LEAD_STATUSES, LEAD_STATUS_LABELS, LEAD_STATUS_COLORS, LISTING_STATUS_COLORS, ROLE_LABELS } from "../../lib/api";
import DashboardLayout, { StatCard, EmptyState, Th, Td } from "./DashboardLayout";
import VisitCalendar from "./VisitCalendar";

const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-900",
  pending: "bg-amber-100 text-amber-900",
  rejected: "bg-red-100 text-red-900",
  suspended: "bg-neutral-200 text-neutral-600",
};

const COMMISSION_FLOW = { pending: "approved", approved: "paid" };

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [rules, setRules] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [assignable, setAssignable] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [salesForm, setSalesForm] = useState({ name: "", email: "", password: "" });
  const [ruleForm, setRuleForm] = useState({ name: "", priority: 10, locality: "", listing_type: "", property_type: "", assign_to: "" });
  const [commForm, setCommForm] = useState({ lead_id: "", deal_value: "", rate_percent: "", beneficiary_id: "" });
  const [editingUser, setEditingUser] = useState(null);
  const [editDraft, setEditDraft] = useState({ email: "", phone: "", role: "" });

  const loadStats = useCallback(() => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadProperties = useCallback(() => api.get("/admin/properties").then(({ data }) => setProperties(data.properties)).catch(() => {}), []);
  const loadUsers = useCallback(() => api.get("/admin/users", { params: userFilter ? { status: userFilter } : {} }).then(({ data }) => setUsers(data.users)).catch(() => {}), [userFilter]);
  const loadLeads = useCallback(() => api.get("/leads").then(({ data }) => setLeads(data.leads)).catch(() => {}), []);
  const loadRules = useCallback(() => api.get("/admin/routing-rules").then(({ data }) => setRules(data.rules)).catch(() => {}), []);
  const loadCommissions = useCallback(() => api.get("/commissions").then(({ data }) => setCommissions(data.commissions)).catch(() => {}), []);
  const loadAssignable = useCallback(() => api.get("/leads/assignable").then(({ data }) => setAssignable(data.users)).catch(() => {}), []);

  useEffect(() => { loadStats(); loadAssignable(); }, [loadStats, loadAssignable]);
  useEffect(() => {
    if (tab === "verification") loadProperties();
    if (tab === "users") loadUsers();
    if (tab === "leads" || tab === "calendar") loadLeads();
    if (tab === "rules") loadRules();
    if (tab === "commissions") { loadCommissions(); loadLeads(); }
  }, [tab, loadProperties, loadUsers, loadLeads, loadRules, loadCommissions]);

  const act = async (fn, okMsg, reloaders) => {
    try {
      await fn();
      toast.success(okMsg);
      reloaders.forEach((r) => r());
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const startEditUser = (u) => {
    setEditingUser(u.id);
    setEditDraft({ email: u.email, phone: u.phone || "", role: u.role });
  };

  const saveUserEdit = async (id) => {
    try {
      await api.patch(`/admin/users/${id}`, editDraft);
      toast.success("Contact details updated — alerts now go to these");
      setEditingUser(null);
      loadUsers();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const wonLeads = leads.filter((l) => l.status === "won");

  return (
    <DashboardLayout
      title="Control Room"
      activeTab={tab}
      onTabChange={setTab}
      tabs={[
        { key: "overview", label: "Overview", icon: LayoutGrid },
        { key: "verification", label: "Verification", icon: ShieldCheck, badge: stats?.properties?.pending_review || 0 },
        { key: "users", label: "Users & Partners", icon: Users, badge: stats?.pending_vendors || 0 },
        { key: "leads", label: "Lead Pipeline", icon: Inbox },
        { key: "calendar", label: "Visit Calendar", icon: CalendarDays },
        { key: "rules", label: "Routing Rules", icon: GitBranch },
        { key: "commissions", label: "Commissions", icon: IndianRupee },
      ]}
    >
      {tab === "overview" && stats && (
        <div data-testid="admin-overview">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard testid="stat-total-users" label="Total Users" value={Object.values(stats.users || {}).reduce((a, b) => a + b, 0)} sub={`${stats.pending_vendors} awaiting approval`} />
            <StatCard testid="stat-live-listings" label="Live Listings" value={stats.properties?.published || 0} sub={`${stats.properties?.pending_review || 0} in review`} />
            <StatCard testid="stat-total-leads" label="Total Leads" value={Object.values(stats.leads || {}).reduce((a, b) => a + b, 0)} sub={`${stats.leads?.won || 0} won`} />
            <StatCard testid="stat-commission-pending" label="Commission Payable" value={formatINR(stats.commissions?.pending || 0)} sub={`${formatINR(stats.commissions?.paid || 0)} paid out`} />
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="dash-card p-6" data-testid="lead-funnel">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">Pipeline Funnel</p>
              <div className="mt-4 space-y-2">
                {LEAD_STATUSES.map((s) => {
                  const total = Object.values(stats.leads || {}).reduce((a, b) => a + b, 0) || 1;
                  const count = stats.leads?.[s] || 0;
                  return (
                    <div key={s} className="flex items-center gap-3">
                      <span className="w-24 font-mono text-[10px] uppercase text-black/50">{LEAD_STATUS_LABELS[s]}</span>
                      <div className="h-5 bg-black" style={{ width: `${Math.max(3, (count / total) * 100)}%` }} />
                      <span className="font-mono text-[10px] text-black/60">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="dash-card p-6" data-testid="user-breakdown">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">Users by Role</p>
              <div className="mt-4 space-y-3">
                {Object.entries(stats.users || {}).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between border-b border-black/5 pb-2">
                    <span className="text-sm text-black/70">{ROLE_LABELS[role] || role}</span>
                    <span className="font-serif text-2xl">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "verification" && (
        <div className="dash-card overflow-x-auto" data-testid="verification-queue">
          {properties.length === 0 ? <EmptyState title="Nothing to review" hint="New partner listings land here." /> : (
            <table className="w-full min-w-[760px]">
              <thead><tr><Th>Listing</Th><Th>Partner</Th><Th>Price</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
              <tbody>
                {properties.map((p) => (
                  <tr key={p.id} data-testid={`verification-row-${p.id}`}>
                    <Td>
                      <p className="font-medium text-black">{p.title}</p>
                      <p className="text-[11px] text-black/45">{p.type} · {p.locality} · {p.beds} bed</p>
                      {p.status === "rejected" && p.rejection_reason && <p className="mt-1 text-[11px] text-red-600">Reason: {p.rejection_reason}</p>}
                    </Td>
                    <Td><p>{p.owner_name}</p><p className="text-[11px] text-black/45">{p.owner_company}</p></Td>
                    <Td className="font-medium">{formatINR(p.price, p.price_unit)}</Td>
                    <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LISTING_STATUS_COLORS[p.status]}`}>{p.status.replace("_", " ")}</span></Td>
                    <Td>
                      <div className="flex flex-wrap gap-2">
                        {p.status !== "published" && (
                          <button className="dash-btn-ghost" data-testid={`approve-listing-${p.id}`} onClick={() => act(() => api.post(`/admin/properties/${p.id}/verify`, { action: "approve" }), "Listing published", [loadProperties, loadStats])}>Approve</button>
                        )}
                        {p.status !== "rejected" && (
                          <button className="dash-btn-ghost" data-testid={`reject-listing-${p.id}`} onClick={() => {
                            const reason = window.prompt("Rejection reason (shown to the partner):");
                            if (reason !== null) act(() => api.post(`/admin/properties/${p.id}/verify`, { action: "reject", reason }), "Listing rejected", [loadProperties, loadStats]);
                          }}>Reject</button>
                        )}
                        <button className="dash-btn-ghost" data-testid={`feature-listing-${p.id}`} onClick={() => act(() => api.post(`/admin/properties/${p.id}/feature`), p.featured ? "Unfeatured" : "Featured", [loadProperties])}>
                          {p.featured ? "★ Featured" : "☆ Feature"}
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "users" && (
        <div data-testid="users-panel">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-2">
              {["", "pending", "active", "suspended"].map((s) => (
                <button key={s || "all"} onClick={() => setUserFilter(s)} data-testid={`user-filter-${s || "all"}`} className={`dash-btn-ghost ${userFilter === s ? "border-black bg-black text-white" : ""}`}>
                  {s || "All"}
                </button>
              ))}
            </div>
          </div>
          <div className="dash-card mb-6 p-6" data-testid="create-sales-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">Add Internal Sales Member — alerts go to this email & phone</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
              <input className="dash-input" placeholder="Name" value={salesForm.name} onChange={(e) => setSalesForm({ ...salesForm, name: e.target.value })} data-testid="sales-name-input" />
              <input className="dash-input" placeholder="Email" value={salesForm.email} onChange={(e) => setSalesForm({ ...salesForm, email: e.target.value })} data-testid="sales-email-input" />
              <input className="dash-input" placeholder="Password" value={salesForm.password} onChange={(e) => setSalesForm({ ...salesForm, password: e.target.value })} data-testid="sales-password-input" />
              <button className="dash-btn" data-testid="sales-create-button" onClick={() => act(
                () => api.post("/admin/users", { ...salesForm, role: "internal_sales" }),
                "Sales member added",
                [loadUsers, loadStats],
              ).then(() => setSalesForm({ name: "", email: "", password: "" }))}>Create</button>
            </div>
          </div>
          <div className="dash-card overflow-x-auto">
            <table className="w-full min-w-[760px]" data-testid="users-table">
              <thead><tr><Th>User</Th><Th>Role</Th><Th>Status</Th><Th>Joined</Th><Th>Actions</Th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <Td>
                      {editingUser === u.id ? (
                        <div className="space-y-2" data-testid={`user-edit-form-${u.id}`}>
                          <input className="dash-input" value={editDraft.email} onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })} placeholder="Alert email" data-testid={`user-edit-email-${u.id}`} />
                          <input className="dash-input" value={editDraft.phone} onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value })} placeholder="WhatsApp number (+91…)" data-testid={`user-edit-phone-${u.id}`} />
                          <select className="dash-input" value={editDraft.role} onChange={(e) => setEditDraft({ ...editDraft, role: e.target.value })} data-testid={`user-edit-role-${u.id}`}>
                            <option value="buyer">Buyer / Tenant</option>
                            <option value="agent">Agent / Broker</option>
                            <option value="builder">Builder / Developer</option>
                            <option value="internal_sales">Internal Sales</option>
                          </select>
                          <div className="flex gap-2">
                            <button className="dash-btn" onClick={() => saveUserEdit(u.id)} data-testid={`user-edit-save-${u.id}`}>Save</button>
                            <button className="dash-btn-ghost" onClick={() => setEditingUser(null)} data-testid={`user-edit-cancel-${u.id}`}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-black">{u.name}</p>
                          <p className="text-[11px] text-black/45">{u.email}{u.company ? ` · ${u.company}` : ""}</p>
                          {u.phone && <p className="text-[11px] text-black/45">{u.phone}</p>}
                        </>
                      )}
                    </Td>
                    <Td>{ROLE_LABELS[u.role]}</Td>
                    <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${STATUS_BADGE[u.status]}`}>{u.status}</span></Td>
                    <Td>{new Date(u.created_at).toLocaleDateString("en-IN")}</Td>
                    <Td>
                      {u.role !== "super_admin" && editingUser !== u.id && (
                        <div className="flex flex-wrap gap-2">
                          <button className="dash-btn-ghost" data-testid={`edit-user-${u.id}`} onClick={() => startEditUser(u)}><Pencil size={11} /> Edit</button>
                          {u.status !== "active" && <button className="dash-btn-ghost" data-testid={`approve-user-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/status`, { status: "active" }), "User activated", [loadUsers, loadStats])}>Approve</button>}
                          {u.status === "active" && <button className="dash-btn-ghost" data-testid={`suspend-user-${u.id}`} onClick={() => act(() => api.post(`/admin/users/${u.id}/status`, { status: "suspended" }), "User suspended", [loadUsers, loadStats])}>Suspend</button>}
                          {u.status === "pending" && <button className="dash-btn-ghost" data-testid={`reject-user-${u.id}`} onClick={() => {
                            const reason = window.prompt("Rejection reason (shown to the applicant):");
                            if (reason !== null) act(() => api.post(`/admin/users/${u.id}/status`, { status: "rejected", reason }), "Application rejected", [loadUsers, loadStats]);
                          }}>Reject</button>}
                        </div>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="dash-card overflow-x-auto" data-testid="admin-leads-table">
          {leads.length === 0 ? <EmptyState title="No leads yet" hint="Enquiries from the marketplace appear here." /> : (
            <table className="w-full min-w-[860px]">
              <thead><tr><Th>Lead</Th><Th>Property</Th><Th>Status</Th><Th>Assigned To</Th><Th>Reassign</Th></tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                    <Td><p className="font-medium text-black">{l.name}</p><p className="text-[11px] text-black/45">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>{l.routed_by_rule && <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-black/40">via {l.routed_by_rule}</p>}</Td>
                    <Td>{l.property_title}<p className="text-[11px] text-black/45">{l.property_locality}</p></Td>
                    <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${LEAD_STATUS_COLORS[l.status]}`}>{LEAD_STATUS_LABELS[l.status]}</span></Td>
                    <Td>{l.assigned_to_name || <span className="text-black/35">Unassigned</span>}</Td>
                    <Td>
                      <select
                        className="dash-input w-40"
                        value=""
                        data-testid={`assign-select-${l.id}`}
                        onChange={(e) => e.target.value && act(() => api.post(`/leads/${l.id}/assign`, { user_id: e.target.value }), "Lead reassigned", [loadLeads])}
                      >
                        <option value="">Assign to…</option>
                        {assignable.map((a) => <option key={a.id} value={a.id}>{a.name} ({ROLE_LABELS[a.role]})</option>)}
                      </select>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "calendar" && <VisitCalendar leads={leads} />}

      {tab === "rules" && (
        <div data-testid="routing-rules-panel">
          <div className="dash-card mb-6 p-6" data-testid="create-rule-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">New Routing Rule — first match wins, then round-robin to the sales desk</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <input className="dash-input" placeholder="Rule name" value={ruleForm.name} onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })} data-testid="rule-name-input" />
              <input className="dash-input" placeholder="Locality (optional)" value={ruleForm.locality} onChange={(e) => setRuleForm({ ...ruleForm, locality: e.target.value })} data-testid="rule-locality-input" />
              <select className="dash-input" value={ruleForm.listing_type} onChange={(e) => setRuleForm({ ...ruleForm, listing_type: e.target.value })} data-testid="rule-listing-type">
                <option value="">Sale + Lease</option><option value="sale">Sale</option><option value="rent">Lease</option>
              </select>
              <select className="dash-input" value={ruleForm.property_type} onChange={(e) => setRuleForm({ ...ruleForm, property_type: e.target.value })} data-testid="rule-property-type">
                <option value="">Any type</option>{["Apartment", "Villa", "Penthouse", "Builder Floor", "Plot"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <select className="dash-input" value={ruleForm.assign_to} onChange={(e) => setRuleForm({ ...ruleForm, assign_to: e.target.value })} data-testid="rule-assign-to">
                <option value="">Assign to…</option>
                {assignable.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button className="dash-btn" data-testid="rule-create-button" onClick={() => {
                if (!ruleForm.name || !ruleForm.assign_to) return toast.error("Name and assignee are required");
                act(() => api.post("/admin/routing-rules", ruleForm), "Rule created", [loadRules])
                  .then(() => setRuleForm({ name: "", priority: 10, locality: "", listing_type: "", property_type: "", assign_to: "" }));
              }}>Add Rule</button>
            </div>
          </div>
          <div className="dash-card overflow-x-auto">
            {rules.length === 0 ? <EmptyState title="No rules" hint="Without rules, leads round-robin to the sales desk." /> : (
              <table className="w-full min-w-[680px]" data-testid="rules-table">
                <thead><tr><Th>Rule</Th><Th>Match</Th><Th>Routes To</Th><Th>State</Th><Th></Th></tr></thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                      <Td className="font-medium text-black">{r.name}</Td>
                      <Td className="font-mono text-[11px]">{[r.locality, r.listing_type, r.property_type].filter(Boolean).join(" · ") || "All leads"}</Td>
                      <Td>{r.assign_to_name}</Td>
                      <Td>
                        <button className="dash-btn-ghost" data-testid={`rule-toggle-${r.id}`} onClick={() => act(() => api.patch(`/admin/routing-rules/${r.id}`, { name: r.name, priority: r.priority, locality: r.locality, listing_type: r.listing_type, property_type: r.property_type, assign_to: r.assign_to, active: !r.active }), r.active ? "Rule paused" : "Rule activated", [loadRules])}>
                          {r.active ? "Active" : "Paused"}
                        </button>
                      </Td>
                      <Td><button className="dash-btn-ghost" data-testid={`rule-delete-${r.id}`} onClick={() => act(() => api.delete(`/admin/routing-rules/${r.id}`), "Rule deleted", [loadRules])}>Delete</button></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === "commissions" && (
        <div data-testid="commissions-panel">
          <div className="dash-card mb-6 p-6" data-testid="create-commission-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-black/45">Log a Commission — from a won deal</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <select className="dash-input" value={commForm.lead_id} onChange={(e) => setCommForm({ ...commForm, lead_id: e.target.value })} data-testid="commission-lead-select">
                <option value="">Won deal…</option>
                {wonLeads.map((l) => <option key={l.id} value={l.id}>{l.property_title} — {l.name}</option>)}
              </select>
              <input className="dash-input" type="number" placeholder="Deal value (₹)" value={commForm.deal_value} onChange={(e) => setCommForm({ ...commForm, deal_value: e.target.value })} data-testid="commission-value-input" />
              <input className="dash-input" type="number" placeholder="Rate %" value={commForm.rate_percent} onChange={(e) => setCommForm({ ...commForm, rate_percent: e.target.value })} data-testid="commission-rate-input" />
              <select className="dash-input" value={commForm.beneficiary_id} onChange={(e) => setCommForm({ ...commForm, beneficiary_id: e.target.value })} data-testid="commission-beneficiary-select">
                <option value="">Beneficiary…</option>
                {assignable.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <button className="dash-btn" data-testid="commission-create-button" onClick={() => {
                if (!commForm.deal_value || !commForm.rate_percent || !commForm.beneficiary_id) return toast.error("Value, rate and beneficiary are required");
                act(() => api.post("/commissions", { ...commForm, deal_value: Number(commForm.deal_value), rate_percent: Number(commForm.rate_percent), lead_id: commForm.lead_id || null }), "Commission logged", [loadCommissions, loadStats])
                  .then(() => setCommForm({ lead_id: "", deal_value: "", rate_percent: "", beneficiary_id: "" }));
              }}>Log</button>
            </div>
          </div>
          <div className="dash-card overflow-x-auto">
            {commissions.length === 0 ? <EmptyState title="No commissions yet" /> : (
              <table className="w-full min-w-[720px]" data-testid="commissions-table">
                <thead><tr><Th>Deal</Th><Th>Value</Th><Th>Rate</Th><Th>Amount</Th><Th>Beneficiary</Th><Th>Status</Th><Th></Th></tr></thead>
                <tbody>
                  {commissions.map((c) => (
                    <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                      <Td className="font-medium text-black">{c.property_title || "—"}</Td>
                      <Td>{formatINR(c.deal_value)}</Td>
                      <Td>{c.rate_percent}%</Td>
                      <Td className="font-medium text-black">{formatINR(c.amount)}</Td>
                      <Td>{c.beneficiary_name}</Td>
                      <Td><span className={`px-2 py-1 font-mono text-[9px] uppercase tracking-widest ${c.status === "paid" ? "bg-emerald-100 text-emerald-900" : c.status === "approved" ? "bg-amber-100 text-amber-900" : "bg-neutral-200 text-neutral-600"}`}>{c.status}</span></Td>
                      <Td>
                        {COMMISSION_FLOW[c.status] && (
                          <button className="dash-btn-ghost" data-testid={`commission-advance-${c.id}`} onClick={() => act(() => api.patch(`/commissions/${c.id}`, { status: COMMISSION_FLOW[c.status] }), `Marked ${COMMISSION_FLOW[c.status]}`, [loadCommissions, loadStats])}>
                            → {COMMISSION_FLOW[c.status]}
                          </button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
